import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { productAPI, reviewAPI, wishlistAPI } from '../api/services';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import Loading from '../components/common/Loading';
import toast from 'react-hot-toast';
import { FiHeart, FiShoppingCart, FiCheck } from 'react-icons/fi';
import './ProductDetail.css';

export default function ProductDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [inWishlist, setInWishlist] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: '', comment: '' });
  const [editingReviewId, setEditingReviewId] = useState(null);

  useEffect(() => {
    setLoading(true);
    productAPI.getBySlug(slug).then(res => {
      const p = res.data.data;
      setProduct(p);
      if (p.variants?.length > 0) setSelectedVariant(p.variants[0]);
      reviewAPI.getForProduct(p.id).then(r => {
        const list = r.data.data.content || [];
        setReviews(list);
        const myReview = user ? list.find(rv => rv.userId === user.id) : null;
        if (myReview) {
          setReviewForm({ rating: myReview.rating, title: myReview.title || '', comment: myReview.comment || '' });
          setEditingReviewId(myReview.id);
        }
      }).catch(() => {});
      if (user) {
        wishlistAPI.check(p.id).then(r => setInWishlist(r.data.data)).catch(() => {});
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [slug, user]);

  const handleAddToCart = () => {
    if (!user) { toast.error('Please login first'); return; }
    if (!selectedVariant) { toast.error('Select a size/color'); return; }
    addItem(product.id, selectedVariant.id, quantity);
  };

  const handleWishlist = async () => {
    if (!user) { toast.error('Please login first'); return; }
    try {
      if (inWishlist) {
        await wishlistAPI.remove(product.id);
        setInWishlist(false);
        toast.success('Removed from wishlist');
      } else {
        await wishlistAPI.add(product.id);
        setInWishlist(true);
        toast.success('Added to wishlist');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const handleReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Please login first'); return; }
    try {
      if (editingReviewId) {
        await reviewAPI.update(editingReviewId, reviewForm);
      } else {
        await reviewAPI.create({ productId: product.id, ...reviewForm });
      }
      const r = await reviewAPI.getForProduct(product.id);
      const list = r.data.data.content || [];
      setReviews(list);
      const myReview = list.find(rv => rv.userId === user.id);
      if (myReview) setEditingReviewId(myReview.id);
      toast.success(editingReviewId ? 'Review updated' : 'Review submitted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit review');
    }
  };

  if (loading) return <Loading />;
  if (!product) return <div className="empty-state"><h3>Product not found</h3></div>;

  const images = product.images || [];
  const currentPrice = selectedVariant?.effectivePrice || product.salePrice || product.basePrice;

  // Group variants by color
  const colors = [...new Set(product.variants?.map(v => v.color) || [])];
  const selectedColor = selectedVariant?.color;
  const sizesForColor = product.variants?.filter(v => v.color === selectedColor) || [];

  return (
    <div className="product-detail">
      <div className="pd-container">
        <div className="pd-gallery">
          <div className="pd-main-image">
            {images[selectedImage] ? (
              <img src={images[selectedImage].imageUrl} alt={images[selectedImage].altText || product.name} />
            ) : (
              <div className="pd-no-image">No Image</div>
            )}
          </div>
          {images.length > 1 && (
            <div className="pd-thumbnails">
              {images.map((img, i) => (
                <img key={i} src={img.imageUrl} alt="" className={i === selectedImage ? 'active' : ''}
                  onClick={() => setSelectedImage(i)} />
              ))}
            </div>
          )}
        </div>

        <div className="pd-info">
          {product.brand && <span className="pd-brand">{product.brand}</span>}
          <h1>{product.name}</h1>

          <div className="pd-price">
            <span className="pd-current-price">₹{currentPrice}</span>
            {product.salePrice && product.salePrice < product.basePrice && (
              <>
                <span className="pd-old-price">₹{product.basePrice}</span>
                <span className="pd-discount">{Math.round((1 - product.salePrice / product.basePrice) * 100)}% OFF</span>
              </>
            )}
          </div>

          {product.avgRating > 0 && (
            <div className="pd-rating">
              {'★'.repeat(Math.round(product.avgRating))}{'☆'.repeat(5 - Math.round(product.avgRating))}
              <span>({product.reviewCount} reviews)</span>
            </div>
          )}

          {colors.length > 0 && (
            <div className="pd-option">
              <label>Color</label>
              <div className="pd-colors">
                {colors.map(c => {
                  const variant = product.variants.find(v => v.color === c);
                  return (
                    <button key={c}
                      className={`color-swatch ${selectedColor === c ? 'active' : ''}`}
                      style={variant?.colorHex ? { backgroundColor: variant.colorHex } : {}}
                      onClick={() => {
                        const v = product.variants.find(v2 => v2.color === c);
                        setSelectedVariant(v);
                      }}
                      title={c}
                    >{!variant?.colorHex ? c : ''}</button>
                  );
                })}
              </div>
            </div>
          )}

          {sizesForColor.length > 0 && (
            <div className="pd-option">
              <label>Size</label>
              <div className="pd-sizes">
                {sizesForColor.map(v => (
                  <button key={v.id}
                    className={`size-btn ${selectedVariant?.id === v.id ? 'active' : ''} ${v.stockQuantity <= 0 ? 'disabled' : ''}`}
                    onClick={() => v.stockQuantity > 0 && setSelectedVariant(v)}
                    disabled={v.stockQuantity <= 0}>
                    {v.size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {selectedVariant && (
            <p className="pd-stock">
              {selectedVariant.stockQuantity > 0
                ? <span className="in-stock"><FiCheck /> In Stock ({selectedVariant.stockQuantity})</span>
                : <span className="out-of-stock">Out of Stock</span>}
            </p>
          )}

          <div className="pd-actions">
            <div className="pd-qty">
              <button onClick={() => setQuantity(q => Math.max(1, q - 1))}>-</button>
              <span>{quantity}</span>
              <button onClick={() => setQuantity(q => q + 1)}>+</button>
            </div>
            <button className="btn btn-primary btn-lg" onClick={handleAddToCart}
              disabled={!selectedVariant || selectedVariant.stockQuantity <= 0}>
              <FiShoppingCart /> Add to Cart
            </button>
            <button className={`btn btn-outline btn-icon ${inWishlist ? 'active-wish' : ''}`} onClick={handleWishlist}>
              <FiHeart />
            </button>
          </div>

          {product.description && (
            <div className="pd-description">
              <h3>Description</h3>
              <p>{product.description}</p>
            </div>
          )}

          <div className="pd-meta">
            {product.material && <p><strong>Material:</strong> {product.material}</p>}
            {product.careInstructions && <p><strong>Care:</strong> {product.careInstructions}</p>}
            {product.sku && <p><strong>SKU:</strong> {product.sku}</p>}
          </div>
        </div>
      </div>

      <div className="pd-reviews">
        <h2>Customer Reviews ({reviews.length})</h2>
        {user && (
          <form className="review-form" onSubmit={handleReview}>
            <p className="review-form-label">{editingReviewId ? 'Update your review' : 'Write a review'}</p>
            <div className="review-rating-input">
              {[1, 2, 3, 4, 5].map(n => (
                <button type="button" key={n} className={reviewForm.rating >= n ? 'star active' : 'star'}
                  onClick={() => setReviewForm(f => ({ ...f, rating: n }))}>★</button>
              ))}
            </div>
            <input type="text" placeholder="Review title (optional)" value={reviewForm.title}
              onChange={e => setReviewForm(f => ({ ...f, title: e.target.value }))} />
            <textarea placeholder="Write your review (optional)..." value={reviewForm.comment}
              onChange={e => setReviewForm(f => ({ ...f, comment: e.target.value }))} rows={3} />
            <button type="submit" className="btn btn-primary btn-sm">{editingReviewId ? 'Update Review' : 'Submit Review'}</button>
          </form>
        )}
        <div className="review-list">
          {reviews.map(r => (
            <div key={r.id} className="review-item">
              <div className="review-header">
                <span className="review-stars">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                <strong>{r.userName}</strong>
                {r.verifiedPurchase && <span className="verified">✓ Verified</span>}
              </div>
              {r.title && <p className="review-title">{r.title}</p>}
              {r.comment && <p className="review-comment">{r.comment}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
